import { Link } from 'react-router-dom';
import { ListPage, type Column } from '../../components/ListPage';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, customerShort } from '../../lib/format';
import { Pill } from '../../components/ui';

// 售后返工（历史 · 只读）：早期单件模型 rework_records(type='customer_return') 的查询视图。
// 新建入口已统一到「售后返修（返修批次）」/rework-batches，避免两个入口继续创建不同数据模型。
// 本页仅保留历史记录的查询能力；新数据不再从此处创建。

type ReworkRow = {
  id: string;
  number?: string;
  reason?: string;
  status?: string;
  created_at?: string;
  original_delivery_number?: string;
  original_contract_number?: string;
  original_inspection_number?: string;
  customer_serial?: string;
  customer_name?: string;
};

const STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' }> = {
  draft: { label: '待返工', tone: 'warn' },
  in_progress: { label: '返工中', tone: 'info' },
  completed: { label: '已完成', tone: 'ok' },
};

export function AfterSalesPage() {
  const { user } = useAuth();
  const canCreate = can(user, 'rework-batches', 'create');

  const columns: Column<ReworkRow>[] = [
    { header: '返修单号', render: (r) => <span className="num strong">{r.number || r.id}</span> },
    { header: '客户', render: (r) => <span title={r.customer_name || ''}>{customerShort(r.customer_name)}</span> },
    { header: '序列号', render: (r) => <span className="muted">{r.customer_serial || '—'}</span> },
    { header: '原交货单', render: (r) => <span className="muted">{r.original_delivery_number || '—'}</span> },
    { header: '原合同', render: (r) => <span className="muted">{r.original_contract_number || '—'}</span> },
    { header: '原因', render: (r) => <span className="muted">{r.reason || '—'}</span> },
    { header: '日期', render: (r) => <span className="muted">{dateLabel(r.created_at)}</span> },
    {
      header: '状态',
      render: (r) => {
        const s = STATUS[r.status || 'draft'] || STATUS.draft;
        return <Pill tone={s.tone} label={s.label} />;
      },
    },
  ];

  return (
    <ListPage<ReworkRow>
      title="售后返工（历史记录）"
      desc="早期单件返工记录的查询视图（只读）。新建返修请前往「售后返修（返修批次）」。"
      endpoint="/api/asset-return/reworks?type=customer_return"
      columns={columns}
      rowId={(r) => String(r.id)}
      renderDetail={(r) => (
        <div className="k" style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
          原交货 {r.original_delivery_number || '—'} · 原合同 {r.original_contract_number || '—'}
          {r.original_inspection_number ? ` · 原质检 ${r.original_inspection_number}` : ''}
          {' ·'}{r.reason || ''}
        </div>
      )}
      showYear={false}
      actions={canCreate ? (
        <Link className="btn primary" to="/rework-batches">前往「售后返修 · 返修批次」新建</Link>
      ) : undefined}
    />
  );
}
